import {
  Box,
  Divider,
  Grid,
  Portal,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import MultipleComparisonPill from "Components/ComparisonPill/MultipleComparisonPill";
import GlassCard from "Components/GlassCard/GlassCard";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { useAnalyticsContext } from "Providers/AnalyticsProvider";
import { useEffect, useMemo, useState } from "react";
import { useUnmount } from "react-use";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import AnalyticsCardGroup from "features/analytics/components/AnalyticsCardGroup";
import {
  CampaignsDropdown,
  ChartLegendItem,
  getChartColor,
  table4Rows,
  useChartData,
} from "features/analytics";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import SelectEmptyState from "features/common/components/SelectEmptyState";
import { CallSplitOutlined } from "@mui/icons-material";
import {
  ApiCampaignType,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiProjectType,
  ApiYAxisChartType,
} from "@incendium/api";
import Loading from "Components/Loading/Loading";
import { AnimatePresence, motion } from "motion/react";
import { useSearchParam } from "react-use";
import { TChartData } from "Interfaces";
import { AnalyticsSpacing, PageActionPreSlot } from "consts";
import { getDataValueOrZeroByKey } from "Helpers/analytics";
import { useCampaigns } from "Hooks/useCampaigns";
import { useHasProjectType } from "features/project";

const SPLIT_CAMPAIGN_DIM = ApiDimension.DIMENSION_INTERNAL_SPLIT_CAMPAIGN;
const SPLIT_URL_DIM = ApiDimension.DIMENSION_INTERNAL_SPLIT_CAMPAIGN_URL_NAME;
const SPLIT_URL_DIM_URL = ApiDimension.DIMENSION_INTERNAL_SPLIT_CAMPAIGN_URL_URL;
const SPLIT_URL_DIM_NAME_AND_URL = ApiDimension.DIMENSION_INTERNAL_SPLIT_CAMPAIGN_URL_NAME_AND_URL;

const TABLE_AVAILABLE_DIMENSIONS: ApiDimension[] = [
  ApiDimension.DIMENSION_CHANNEL,
  ApiDimension.DIMENSION_SOURCE,
  ApiDimension.DIMENSION_CAMPAIGN,
  ApiDimension.DIMENSION_CONTENT,
  ApiDimension.DIMENSION_TERM,
  ApiDimension.DIMENSION_DEVICE,
  ApiDimension.DIMENSION_BROWSER,
  ApiDimension.DIMENSION_OS,
  ApiDimension.DIMENSION_COUNTRY,
];

const KEY_STAT_METRICS: { name: string; metric: ApiMetric; isPercentage: boolean }[] = [
  { name: "Pageviews", metric: ApiMetric.METRIC_PAGEVIEWS_COUNT, isPercentage: false },
  { name: "Effective Pageview Rate", metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE, isPercentage: true },
  { name: "Avg Time on Page", metric: ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE, isPercentage: false },
  { name: "Viewed %", metric: ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE, isPercentage: true },
  { name: "Scroll %", metric: ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE, isPercentage: true },
  { name: "Interactions", metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT, isPercentage: false },
  { name: "Interaction Rate", metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE, isPercentage: true },
  { name: "Impacted Conversions", metric: ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED, isPercentage: false },
  { name: "Impacted Conversion Rate", metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED, isPercentage: true },
  { name: "Session Conversions", metric: ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST, isPercentage: false },
  { name: "Session Conversion Rate", metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST, isPercentage: true },
  { name: "LP Session Conversion Rate", metric: ApiMetric.METRIC_LANDING_PAGE_SESSION_MACRO_CONVERSION_RATE, isPercentage: true },
];

function CampaignSplitOverviewPage() {
  const { selectedProject } = useSelectedProject();
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const { chartOutput } = useFromToContext();
  const { campaigns } = useCampaigns();
  const campaignParam = useSearchParam("campaign");
  const hasProjectType = useHasProjectType();
  const { setColourMap } = useAnalyticsContext();
  const theme = useTheme();
  const dimensionToName = useDimensionName();

  useEffect(() => {
    if (!campaigns?.length || !campaignParam) return;
    const hasCampaign = campaigns.find((c) => c.id === Number(campaignParam));
    if (hasCampaign?.name) {
      setSelectedCampaign(hasCampaign.name);
    }
  }, [campaigns, campaignParam]);

  const tableMetrics = useMemo(() => {
    const arr = [
      ApiMetric.METRIC_LEADS_COUNT,
      ApiMetric.METRIC_PAGEVIEWS_COUNT,
      ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
      ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
      ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED,
      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
      ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
    ];
    if (hasProjectType(ApiProjectType.TYPE_ECOMMERCE) || true) {
      arr.push(
        ...[
          ApiMetric.METRIC_REVENUE_IMPACTED,
          ApiMetric.METRIC_SALES_COUNT_IMPACTED,
          ApiMetric.METRIC_AVERAGE_SALE_VALUE_IMPACTED,
        ]
      );
    }
    return arr;
  }, [hasProjectType]);

  const campaignChart = useMemo(
    () => ({
      name: "",
      dimension: [SPLIT_URL_DIM_NAME_AND_URL],
      orderBy: SPLIT_URL_DIM_NAME_AND_URL,
      yAxisKeys: [
        {
          fields: [
            ApiMetric.METRIC_PAGEVIEWS_COUNT,
            ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
            ApiMetric.METRIC_LEADS_COUNT,
            ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
            ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
            ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
            ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
            ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE,
            ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
            ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED,
            ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
            ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
            ApiMetric.METRIC_LANDING_PAGE_SESSION_MACRO_CONVERSION_RATE,
          ],
        },
      ],
      includeSubdomains: true,
      attributes: selectedCampaign
        ? [{ key: SPLIT_CAMPAIGN_DIM, value: selectedCampaign }]
        : [],
    }),
    [selectedCampaign]
  );

  const { chartData: keyData, loading } = useChartData(
    selectedProject,
    campaignChart,
    chartOutput
  );

  const splitUrlRows = useMemo(() => {
    if (!keyData?.data || !selectedCampaign) return [];
    return (keyData.data as TChartData[]);
  }, [keyData, selectedCampaign]);

  const colourMap = useMemo(() => {
    const map: Record<string, string> = {};
    splitUrlRows.forEach((row, i) => {
      const urlLabel =
        (row[SPLIT_URL_DIM_NAME_AND_URL] as string) ?? (row.name as string) ?? "—";
      const color = getChartColor(i, theme.palette.charts as string[]);
      map[String(urlLabel)] = color;
      const namePart = String(urlLabel).split("|")[0]?.trim() || String(urlLabel);
      map[namePart] = color;
    });
    return map;
  }, [splitUrlRows, theme.palette.charts]);

  useEffect(() => {
    setColourMap(colourMap);
  }, [colourMap, setColourMap]);

  useUnmount(() => {
    setColourMap({});
  });

  const keyStats = useMemo(() => {
    return KEY_STAT_METRICS.map((stat) => ({
      name: stat.name,
      metric: stat.metric,
      isPercentage: stat.isPercentage,
      items: splitUrlRows.map((row) => {
        const urlLabel =
          (row[SPLIT_URL_DIM_NAME_AND_URL] as string) ?? (row.name as string) ?? "—";
        const value = getDataValueOrZeroByKey(row, stat.metric as keyof TChartData);
        return {
          label: String(urlLabel),
          value,
          color: colourMap[String(urlLabel)],
        };
      }),
    }));
  }, [splitUrlRows, colourMap]);

  if (!selectedCampaign) {
    return (
      <SelectEmptyState
        icon={
          <CallSplitOutlined
            sx={{ fontSize: 40, color: "primary.main", opacity: 0.9 }}
          />
        }
        title="Select a split campaign to compare performance"
        dropdown={
          <CampaignsDropdown
            sx={{ width: "100%", maxWidth: 320 }}
            label="Campaign"
            variant="outlined"
            value={selectedCampaign}
            onChange={(name) => setSelectedCampaign(name)}
            campaignTypes={ApiCampaignType.CAMPAIGN_TYPE_SPLIT}
          />
        }
        table={
          <AnalyticsCard
            onClick={{
              [SPLIT_CAMPAIGN_DIM]: [
                {
                  text: "Select Campaign",
                  fn: (p) => setSelectedCampaign(p || ""),
                },
              ],
            }}
            chart={{
              name: "Split Campaign Insights",
              dimension: [SPLIT_CAMPAIGN_DIM],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                    ApiMetric.METRIC_LEADS_COUNT,
                    ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
                    ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED,
                    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
                    ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
                  ],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                rowsPerPage: 0,
                noTitle: true,
              },
            }}
          />
        }
      />
    );
  }

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <Loading text="Fetching campaign data" />
      ) : (
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Portal container={() => document.getElementById(PageActionPreSlot)}>
            <CampaignsDropdown
              sx={{ width: 270 }}
              label="Campaign"
              variant="outlined"
              size="small"
              value={selectedCampaign}
              onChange={(name) => setSelectedCampaign(name)}
              campaignTypes={ApiCampaignType.CAMPAIGN_TYPE_SPLIT}
            />
          </Portal>
          <Grid container spacing={AnalyticsSpacing} mb={4}>
            <Grid item xs={12} container spacing={AnalyticsSpacing}>
              <Grid item xs={3} >
                <GlassCard
                  noShadow
                  sx={{ height: "100%" }}
                  boxProps={{
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                    Key Statistics
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ lineHeight: 1.2 }}
                    color={"secondary"}
                    fontWeight={500}
                    mb={2}
                  >
                    Compare performance across split endpoints
                  </Typography>
                  <Divider />
                  <Box>
                    <Stack
                      my={2}
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {splitUrlRows.map((row, index) => {
                        const urlLabel =
                          (row[SPLIT_URL_DIM_NAME_AND_URL] as string) ?? (row.name as string) ?? "—";
                        return (
                          <ChartLegendItem
                            key={String(urlLabel)}
                            item={String(urlLabel)}
                            index={index}
                            fill={colourMap[String(urlLabel)]}
                          />
                        );
                      })}
                    </Stack>
                    {splitUrlRows.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 3, textAlign: "center" }}
                      >
                        No split data for this campaign yet
                      </Typography>
                    ) : (
                      keyStats.map((stat) => (
                        <MultipleComparisonPill
                          key={stat.metric}
                          title={stat.name}
                          items={stat.items}
                          metric={stat.metric}
                          isPercentage={stat.isPercentage}
                        />
                      ))
                    )}
                  </Box>
                </GlassCard>
              </Grid>

              <Grid item xs={6} container spacing={AnalyticsSpacing}>
                <Grid item xs={12} >
                  <AnalyticsCard
                    chart={{
                      name: "Traffic",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        SPLIT_URL_DIM,
                      ],
                      orderBy: `${SPLIT_URL_DIM}`,
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: [{
                        key: SPLIT_CAMPAIGN_DIM,
                        value: selectedCampaign,
                      }],
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} >
                  <AnalyticsCard
                    chart={{
                      name: "Engagement",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        SPLIT_URL_DIM,
                      ],
                      orderBy: `${SPLIT_URL_DIM}`,
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: [{
                        key: SPLIT_CAMPAIGN_DIM,
                        value: selectedCampaign,
                      }],
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE],
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
          
                <Grid item xs={12} >
                  <AnalyticsCard
                    chart={{
                      name: "Impacted Conversion Rate",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        SPLIT_URL_DIM,
                      ],
                      orderBy: `${SPLIT_URL_DIM}`,
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: [{
                        key: SPLIT_CAMPAIGN_DIM,
                        value: selectedCampaign,
                      }],
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED],
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
          
              </Grid>
              <Grid item xs={3} container spacing={AnalyticsSpacing}>
                <Grid item xs={12} >
                  <AnalyticsCard
                    chart={{
                      name: "Total Traffic",
                      dimension: [
                        SPLIT_URL_DIM,
                      ],
                      orderBy: `${SPLIT_URL_DIM}`,
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.GRAPH,
                      attributes: [{
                        key: SPLIT_CAMPAIGN_DIM,
                        value: selectedCampaign,
                      }],
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                          chart: {
                            [ApiMetric.METRIC_PAGEVIEWS_COUNT]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AnalyticsCard
                    chart={{
                      name: "Avg. Engagement Rate",
                      dimension: [
                        SPLIT_URL_DIM,
                      ],
                      orderBy: `${SPLIT_URL_DIM}`,
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.GRAPH,
                      attributes: [{
                        key: SPLIT_CAMPAIGN_DIM,
                        value: selectedCampaign,
                      }],
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE],
                          chart: {
                            [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
          
                <Grid item xs={12} >
                  <AnalyticsCard
                    chart={{
                      name: "Total Impacted Conversion Rate",
                      dimension: [
                        SPLIT_URL_DIM,
                      ],
                      orderBy: `${SPLIT_URL_DIM}`,
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.GRAPH,
                      attributes: [{
                        key: SPLIT_CAMPAIGN_DIM,
                        value: selectedCampaign,
                      }],
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED],
                          chart: {
                            [ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
          
              </Grid>
            
            </Grid>
            <Grid item xs={12}>
              <AnalyticsCardGroup
                title={(d) =>
                  d === ApiDimension.DIMENSION_NOT_SET
                    ? "Performance details"
                    : `Performance by ${dimensionToName(d)}`
                }
                dimensions={[ApiDimension.DIMENSION_NOT_SET, ...TABLE_AVAILABLE_DIMENSIONS]}
                renderBodyWithDimension={(selectedDimension) =>
                  selectedDimension === ApiDimension.DIMENSION_NOT_SET ? (
                    <Box sx={{ height: table4Rows }}>
                      <AnalyticsCard
                        chart={{
                          name: "Performance details",
                          dimension: [SPLIT_URL_DIM_URL],
                          orderBy: `${SPLIT_URL_DIM_URL}`,
                          template: ApiChartTemplate.TABLE,
                          type: ApiChartType.TABLE,
                          attributes: [
                            { key: SPLIT_CAMPAIGN_DIM, value: selectedCampaign ?? "" },
                          ],
                          includeSubdomains: true,
                          yAxisKeys: [
                            {
                              key: "l",
                              fields: tableMetrics,
                            },
                          ],
                          displayOptions: {
                            noFilters: true,
                            noTitle: true,
                            showTotals: true,
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Stack spacing={3}>
                      {splitUrlRows.map((row) => {
                        const urlLabel =
                          (row[SPLIT_URL_DIM_NAME_AND_URL] as string) ??
                          (row.name as string) ??
                          "—";
                        const urlValue =
                          (row[SPLIT_URL_DIM_URL] as string) ??
                          (urlLabel.split("|")[1]?.trim() ?? urlLabel);
                        return (
                          <Box key={urlLabel} sx={{ height: table4Rows }}>
                            <AnalyticsCard
                              chart={{
                                name: urlLabel,
                                dimension: [selectedDimension],
                                orderBy: `${selectedDimension}`,
                                template: ApiChartTemplate.TABLE,
                                type: ApiChartType.TABLE,
                                attributes: [
                                  { key: SPLIT_CAMPAIGN_DIM, value: selectedCampaign ?? "" },
                                  { key: SPLIT_URL_DIM_URL, value: urlValue },
                                ],
                                includeSubdomains: true,
                                yAxisKeys: [
                                  {
                                    key: "l",
                                    fields: tableMetrics,
                                  },
                                ],
                                displayOptions: {
                                  noFilters: true,
                                  showTotals: true,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  )
                }
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </AnimatePresence>
  );
}

export default CampaignSplitOverviewPage;
